/**
 * User Menu Component
 *
 * Dropdown menu for authenticated users in the FloatingNav.
 * Shows user info and logout option.
 */

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { User, LogOut, ChevronDown, Shield, Mail } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useRBAC } from '@/hooks/useRBAC'

export function UserMenu() {
  const { user, logout, isAuthenticated } = useAuth()
  const { isAdmin, roles } = useRBAC()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    setIsOpen(false)
    await logout()
  }

  // Show login button if not authenticated
  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        className="flex items-center gap-2 px-4 py-2 bg-eventus-teal hover:bg-eventus-teal-light text-eventus-navy font-semibold rounded-full transition-all hover:shadow-lg text-sm"
      >
        <User className="w-4 h-4" />
        Sign In
      </Link>
    )
  }

  // Get initials for avatar
  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  return (
    <>
    <div ref={menuRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-white/90 hover:text-white hover:bg-white/20"
      >
        {/* Avatar */}
        <div className="w-7 h-7 bg-eventus-teal rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-eventus-navy">{initials}</span>
        </div>
        <span className="text-white/90 text-sm font-medium hidden sm:block max-w-[100px] truncate">
          {user?.name?.split(' ')[0] || 'User'}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-64 bg-slate-900/70 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* User info */}
            <div className="p-4 border-b border-white/10">
              <p className="text-white font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-white/60 text-sm truncate">{user?.email || ''}</p>

              {/* Role badges */}
              {roles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {isAdmin && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-eventus-teal/20 text-eventus-teal text-xs font-medium rounded">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                  {roles
                    .filter((r) => r !== 'Admin')
                    .slice(0, 2)
                    .map((role) => (
                      <span
                        key={role}
                        className="px-2 py-0.5 bg-white/10 text-white/80 text-xs rounded"
                      >
                        {role.replace('equip_', '').replace('_', ' ')}
                      </span>
                    ))}
                </div>
              )}
            </div>

            {/* Admin menu items */}
            {isAdmin && (
              <>
                <div className="h-px bg-white/10 my-1" />
                <div className="p-2">
                  <Link
                    to="/admin/email-templates"
                    onClick={() => setIsOpen(false)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    Email Templates
                  </Link>
                </div>
              </>
            )}

            {/* Menu items */}
            <div className="h-px bg-white/10 my-1" />
            <div className="p-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  )
}
